import { Share } from '@drift-labs/icons';
import { useEffect, useRef, useState } from 'react';
import { Eye } from 'react-feather';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import useIsMobile from 'src/hooks/useIsMobileScreenSize';
import {
	FundingHistoryData,
	fundingHistorySelector,
} from 'src/selectors/fundingHistorySelector';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import UI_UTILS from 'src/utils/uiUtils';
import useShowAccountValues from '../hooks/useShowAccountValues';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import InlineLoadingBar from './InlineLoadingBar/InlineLoadingBar';
import PaginatedTableV2 from './PaginatedTableV2';
import TableV2 from './Tables/TableV2';
import Tooltip from './Tooltip/Tooltip';

const pageSize = 20;

const FundingHistoryTable = () => {
	const tableGrid =
		'minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(40px,.5fr)';

	const setState = useDriftAccountStore((s) => s.set);
	const currentAccountUserKey = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.userKey
	);
	const currentAccountName = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.name
	);
	const currentAccountPubKey = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.pubKey
	);
	const currentAccountFundingHistory = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.fundingHistory
	);
	const lastUserKey = useRef(currentAccountUserKey ?? '');
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	const fundingHistory: FundingHistoryData[] =
		currentAccountFundingHistory?.initialHistoryLoaded
			? fundingHistorySelector(
					currentAccountFundingHistory.records,
					currentAccountName
			  )
			: undefined;
	const totalHistoryCount = currentAccountFundingHistory?.totalCount ?? 0;

	const [currentFundingHistory, setCurrentFundingHistory] = useState(
		fundingHistory?.slice(0, pageSize) ?? undefined
	);

	const historyIsLoading = currentAccountFundingHistory?.loading ?? false;

	const [firstPageShown, setFirstPageShown] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);

	useEffect(() => {
		if (
			(!currentFundingHistory || currentFundingHistory?.length === 0) &&
			fundingHistory &&
			fundingHistory.length > 0
		) {
			setCurrentFundingHistory(fundingHistory);
		}
	}, [fundingHistory, currentFundingHistory]);

	useEffect(() => {
		if (lastUserKey.current !== currentAccountUserKey) {
			resetState();
			lastUserKey.current = currentAccountUserKey;
		}
	}, [currentAccountUserKey]);

	const resetState = () => {
		setFirstPageShown(0);
		setCurrentPage(0);
		setCurrentFundingHistory([]);
	};

	const isConnected = useWalletIsConnected({
		onDisconnectedCallback: resetState,
	});

	const setUserFundingHistoryLoading = (isLoading: boolean) => {
		setState((s) => {
			s.accounts[currentAccountUserKey].fundingHistory.loading = isLoading;
		});
	};

	const executePageChange = (newPage: number, oldFirstPageShown: number) => {
		if (newPage === currentPage) return;

		if (fundingHistory.length < pageSize * (newPage + 1)) {
			setUserFundingHistoryLoading(true);
			ExchangeHistoryClient.getFundingForUsers(
				[currentAccountPubKey],
				newPage,
				pageSize
			).then((result) => {
				const newRecords = fundingHistorySelector(
					result?.body?.data?.records?.[0],
					currentAccountName
				);
				setCurrentFundingHistory(newRecords);
				setUserFundingHistoryLoading(false);
			});
		} else {
			setCurrentFundingHistory(
				fundingHistory.slice(newPage * pageSize, newPage * pageSize + pageSize)
			);
		}

		setCurrentPage(newPage);
		setFirstPageShown(oldFirstPageShown);
	};

	const isMobile = useIsMobile();

	// TODO-v2 funding rate in these records is borked rn, need to figure out how to calculate
	const headers = [
		'market',
		'direction',
		'position size',
		'payment',
		//'market funding rate',
		'date',
		'',
	];

	const showAccountValues = useShowAccountValues();

	return (
		<PaginatedTableV2
			noBorder
			tableGrid={tableGrid}
			pageSize={pageSize}
			currentPage={currentPage}
			firstPageShown={firstPageShown}
			pageChangeCallback={executePageChange}
			totalItems={totalHistoryCount}
			tableState={{
				records: fundingHistory,
				emptyStateText: 'No Funding History found',
				pageChangeLoading: historyIsLoading,
				id: 'funding_history_table',
				requireAccountCreated: true,
				requireWalletConnect: true,
			}}
			tableContentTop={
				isMobile ? null : (
					<TableV2.HeaderRow
						grid={tableGrid}
						className="w-full text-xs"
						header
						forceBottomBorder
						lastColumnJustify="start"
					>
						{headers.map((label, index) => (
							<>
								{label === 'market funding rate' ? (
									<TableV2.TooltipHeaderCell>
										<Tooltip
											placement="top"
											content={
												<div>
													Average funding paid/collected per interval over the
													unrealized duration.
												</div>
											}
										>
											{label}
										</Tooltip>
									</TableV2.TooltipHeaderCell>
								) : label === 'direction' ? (
									<TableV2.TooltipHeaderCell>
										<Tooltip
											placement="top"
											content={<div>Position direction at time of payment</div>}
										>
											{label}
										</Tooltip>
									</TableV2.TooltipHeaderCell>
								) : label === 'position size' ? (
									<TableV2.TooltipHeaderCell>
										<Tooltip
											placement="top"
											content={
												<div>Position size at the time of funding payment</div>
											}
										>
											{label}
										</Tooltip>
									</TableV2.TooltipHeaderCell>
								) : label === 'payment' ? (
									<TableV2.TooltipHeaderCell>
										<Tooltip placement="top" content={<div>Total payment</div>}>
											{label}
										</Tooltip>
									</TableV2.TooltipHeaderCell>
								) : label.toLowerCase() === 'date' ? (
									<TableV2.TooltipHeaderCell>
										<Tooltip
											placement="top"
											content={
												<div>Time funding payment/collection was realized.</div>
											}
										>
											<div
												onClick={toggleDateDisplayType}
												className={'cursor-pointer'}
											>
												{label} <Eye size={12} className="inline-block" />
											</div>
										</Tooltip>
									</TableV2.TooltipHeaderCell>
								) : (
									// : label === 'market' ? (
									// 	// Market Filter
									// 	<Select.NoBox
									// 		id="tradeHistoryMarketFilter"
									// 		options={[
									// 			{
									// 				label: 'ALL',
									// 				value: '',
									// 			},
									// 			...CurrentPerpMarkets.map((market) => ({
									// 				label: market.symbol,
									// 				value: market.symbol,
									// 			})),
									// 		]}
									// 		selection={selectedMarketFilter}
									// 		onChange={(value) =>
									// 			handleMarketFilterUpdate(value)
									// 		}
									// 		noStyle
									// 		defaultLabel="Market"
									// 		style={{ letterSpacing: '2px' }}
									// 		maxHeight={160}
									// 	/>
									// )
									<TableV2.HeaderCell key={`funding_header_${index}`}>
										{label}
									</TableV2.HeaderCell>
								)}
							</>
						))}
					</TableV2.HeaderRow>
				)
			}
			tableContentMiddle={
				historyIsLoading ? (
					<div
						className={`flex flex-col justify-center items-center xs:h-[40vh] sm:h-full w-full text-center rounded-md xs:pt-8 sm:pt-0`}
					>
						<InlineLoadingBar />
					</div>
				) : (
					<div className="flex flex-col w-full xs:h-[40vh] h-full">
						{isConnected &&
							currentFundingHistory
								// .filter((payment) =>
								// 	payment.market.includes(selectedMarketFilter)
								// )
								?.map((payment, index) => (
									<TableV2.BodyRow
										key={`${index}_${payment.market}`}
										grid={tableGrid}
										lastColumnJustify="start"
										isDataRow
									>
										<TableV2.MarketCell
											key={`market_${index}`}
											marketSymbol={`${payment.market}-PERP`}
										/>
										<TableV2.BodyCell
											key={`direction_${index}`}
											className={
												payment.baseAssetAmount.isNeg()
													? `text-negative-red`
													: `text-positive-green`
											}
										>
											{payment.baseAssetAmount.isNeg() ? 'Short' : 'Long'}
										</TableV2.BodyCell>

										<TableV2.BodyCell key={`size_${index}`}>
											{`${
												showAccountValues
													? payment.baseAssetAmount.toTradePrecision()
													: '∗∗∗∗∗'
											} ${payment.market}`}
										</TableV2.BodyCell>

										<TableV2.AssetCell
											key={`payment_${index}`}
											className={
												payment.size.ltZero()
													? `text-negative-red`
													: `text-positive-green`
											}
											value={payment.size}
											assetSymbol="USDC"
											textOverride={!showAccountValues ? '*****' : null}
											toFixed={4}
											semantic
										/>
										{/* <TableV2.BodyCell
														key={`fundingRate_${index}`}
														className={
															payment.rate.ltZero()
																? `text-negative-red`
																: `text-positive-green`
														}
													>
														<Tooltip
															placement="top"
															content={
																<div>
																	{'  per ' + payment.market.split('-')[0]}
																	{' over ~' +
																		calcRealizedDuration(payment).toFixed(0) +
																		'h'}{' '}
																</div>
															}
														>
															{payment?.rate.prettyPrint()}%
														</Tooltip>
													</TableV2.BodyCell> */}
										<TableV2.DateCell
											key={`fundingDate_${index}`}
											date={new Date(payment.datetime)}
											displayAsTimestamp={displayDatesAsTimestamp}
										></TableV2.DateCell>
										<TableV2.BodyCell className="justify-start">
											<div
												className="hover:cursor-pointer hover:opacity-80"
												onClick={() => {
													UI_UTILS.openUrl(UI_UTILS.getUrlForTx(payment.txSig));
												}}
											>
												<Tooltip placement="top" content={'View transaction'}>
													<Share color="var(--text-label)" />
												</Tooltip>
											</div>
										</TableV2.BodyCell>
									</TableV2.BodyRow>
								))}
					</div>
				)
			}
			// TODO - add this back, need to fix query and market filtering
			// tableContentBottom={
			// 	<div
			// 		className={`text-darkBlue-30 text-xs font-numeral whitespace-nowrap pl-4 py-1 xl:py-2 overflow-hidden justify-between flex`}
			// 	>
			// 		<span className="overflow-hidden text-ellipsis">
			// 			{`Cumulative ${selectedMarketFilter} funding earned: `}
			// 		</span>
			// 		<div
			// 			className={`font-numeral text-xs inline-flex ml-1 ${
			// 				selectedCumFunding >= 0
			// 					? 'text-positive-green'
			// 					: 'text-negative-red'
			// 			}`}
			// 		>
			// 			{NumLib.formatNum.toNotionalDisplay(selectedCumFunding)}
			// 		</div>
			// 	</div>
			// }
		/>
	);
};

export default FundingHistoryTable;
